import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Input from '../components/Input';
import Button from '../components/Button';
import { forgotPassword, resetForgotPassword } from '../services/authService';

const OTP_LENGTH = 6;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email | otp | password | done
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (imageRef.current?.complete) {
      setImageLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (step === 'otp') {
      otpRefs.current[0]?.focus();
    }
  }, [step]);

  function validateEmail() {
    const errors = {};
    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address.';
    }
    return errors;
  }

  function validatePassword() {
    const errors = {};
    if (!password) {
      errors.password = 'New password is required.';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters.';
    }
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password.';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }
    return errors;
  }

  async function handleSendOtp(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors = validateEmail();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await forgotPassword(email.trim());
      if (response.success !== false) {
        setOtp(Array(OTP_LENGTH).fill(''));
        setStep('otp');
      } else {
        setError(response.message || 'Something went wrong.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  function handleOtpChange(index, value) {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setError('');

    if (digit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;

    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i += 1) {
      next[i] = pasted[i];
    }
    setOtp(next);
    setError('');
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH) - 1]?.focus();
  }

  function handleVerifyOtp(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const code = otp.join('');
    if (code.length !== OTP_LENGTH) {
      setError('Please enter the 6-digit OTP.');
      return;
    }

    setPassword('');
    setConfirmPassword('');
    setStep('password');
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors = validatePassword();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await resetForgotPassword({
        email: email.trim(),
        otp: otp.join(''),
        password,
        confirmPassword,
      });

      if (response.success !== false) {
        setStep('done');
      } else {
        setError(response.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  const titles = {
    email: {
      title: 'Forgot Password',
      subtitle: "Enter your email address and we'll send you a link to reset your password.",
    },
    otp: {
      title: 'Enter OTP',
      subtitle: `An OTP has been sent to ${email.trim()}.`,
    },
    password: {
      title: 'Reset Password',
      subtitle: 'Enter your new password below.',
    },
    done: {
      title: 'Password Updated',
      subtitle: 'Your password has been reset successfully. You can now log in.',
    },
  };

  const copy = titles[step];

  return (
    <div className="login-page">
      <div className={`login-left${imageLoaded ? ' is-loaded' : ''}`}>
        {!imageLoaded && <div className="login-left-shimmer" aria-hidden="true" />}
        <img
          ref={imageRef}
          src={`${process.env.PUBLIC_URL}/images/login-left.png`}
          alt="login"
          onLoad={() => setImageLoaded(true)}
        />
      </div>

      <form
        className="login-form"
        onSubmit={
          step === 'email'
            ? handleSendOtp
            : step === 'otp'
              ? handleVerifyOtp
              : step === 'password'
                ? handleChangePassword
                : (e) => {
                    e.preventDefault();
                    navigate('/login', { replace: true });
                  }
        }
      >
        <div className="login-logo">
          <img src={`${process.env.PUBLIC_URL}/images/login-logo.svg`} alt="logo" />
        </div>
        <h2 className="login-title">{copy.title}</h2>
        <p className="login-subtitle">{copy.subtitle}</p>

        {error && <div className="login-error">{error}</div>}

        {step === 'email' && (
          <>
            <Input
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
              }}
              placeholder="Enter your email"
              error={fieldErrors.email}
            />

            <Button type="submit" variant="primary" className="login-btn" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <Link to="/login" className="back-to-login">
              Back to Login
            </Link>
          </>
        )}

        {step === 'otp' && (
          <>
            <div className="otp-field">
              <label className="input-label" htmlFor="otp-0">
                OTP
              </label>
              <div className="otp-inputs" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={index === 0 ? 'otp-0' : undefined}
                    ref={(el) => {
                      otpRefs.current[index] = el;
                    }}
                    className="otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            <Button type="submit" variant="primary" className="login-btn" disabled={isLoading}>
              Verify OTP
            </Button>

            <button
              type="button"
              className="back-to-login back-to-login-btn"
              onClick={() => {
                setError('');
                setFieldErrors({});
                setStep('email');
              }}
            >
              Back
            </button>
          </>
        )}

        {step === 'password' && (
          <>
            <Input
              id="new-password"
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              error={fieldErrors.password}
            />
            <Input
              id="confirm-password"
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              error={fieldErrors.confirmPassword}
            />

            <Button type="submit" variant="primary" className="login-btn" disabled={isLoading}>
              {isLoading ? 'Changing...' : 'Change Password'}
            </Button>

            <button
              type="button"
              className="back-to-login back-to-login-btn"
              onClick={() => {
                setError('');
                setFieldErrors({});
                setStep('otp');
              }}
            >
              Back
            </button>
          </>
        )}

        {step === 'done' && (
          <div className="forgot-success">
            <p>Your password has been changed successfully.</p>
            <Button type="submit" variant="primary" className="login-btn">
              Back to Login
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
